// @oasis-permission-inherits: /jerboa-client-export (jerboa:view)
import { Link, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  formatBytes,
  formatDurationMs,
  formatErrorCode,
  formatFps,
  formatTimestamp,
  statusVariant,
} from "~/modules/jerboa-client-export/formatters";
import { FramerateChart, SuccessRateChart } from "~/modules/jerboa-client-export/metrics-chart";
import {
  fetchJerboaClientExportAnalysisQuery,
  fetchJerboaClientExportsQuery,
} from "~/modules/jerboa-client-export/queries";
import {
  jerboaClientExportStatusSchema,
  type JerboaClientExportStatus,
} from "~/modules/jerboa-client-export/schemas";

const searchSchema = z.object({
  page: z.number().optional(),
  status: jerboaClientExportStatusSchema.optional(),
  projectId: z.string().optional(),
});

export const Route = createFileRoute("/_dash/jerboa-client-export/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { page, status, projectId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();

  const params = useMemo(() => ({ status, projectId }), [status, projectId]);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaClientExportsQuery.useInfiniteQuery({
      params,
    });

  const { data: analysisData } = fetchJerboaClientExportAnalysisQuery.useQuery({
    params: { days: 7 },
  });

  const {
    currentPageIndex,
    currentPageData,
    totalPages,
    onPreviousPage,
    onNextPage,
    onPageChange,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => navigate({ search: { ...params, page: nextPage } }),
  });

  const onStatusChange = (nextStatus: JerboaClientExportStatus | undefined) => {
    navigate({ search: { page: 1, projectId, status: nextStatus } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Client Exports</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      {analysisData && analysisData.analysis.buckets.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="mb-3">
              <p className="text-sm font-medium">Export success rate over the last 7 days</p>
              <p className="text-xs text-muted-foreground">
                successCount / totalCount per 2-hour bucket
              </p>
            </div>
            <SuccessRateChart buckets={analysisData.analysis.buckets} />
          </div>
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="mb-3">
              <p className="text-sm font-medium">Export framerate over the last 7 days</p>
              <p className="text-xs text-muted-foreground">
                Mean / p50 / p90 fps — across exports per 2-hour bucket
              </p>
            </div>
            <FramerateChart buckets={analysisData.analysis.buckets} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Status:</span>
        {(["finish", "error", "cancel"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onStatusChange(status === s ? undefined : s)}
            className="inline-flex"
          >
            <Badge variant={status === s ? statusVariant(s) : "outline"}>{s}</Badge>
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load jerboa client exports</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Project ID</TableHead>
              <TableHead>Resolution</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Output Size</TableHead>
              <TableHead>Framerate (p90)</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageData.page.map((result) => {
              const errorLabel = formatErrorCode(result.errorCode) ?? result.errorCategory;
              return (
                <TableRow
                  key={result.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    router.navigate({
                      to: "/jerboa-client-export/$id",
                      params: { id: result.id },
                      search: { page: currentPageIndex + 1, ...params },
                    })
                  }
                >
                  <TableCell>{formatTimestamp(result.createdAt)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(result.status)}>{result.status}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {result.captionsProjectId ? (
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: result.captionsProjectId }}
                        className="text-blue-600 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {result.captionsProjectId}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {result.resolution.toUpperCase()} {result.width}×{result.height}
                  </TableCell>
                  <TableCell>{formatDurationMs(result.durationMs)}</TableCell>
                  <TableCell>{formatBytes(result.outputSizeBytes)}</TableCell>
                  <TableCell className="text-xs">{formatFps(result.p90PlaybackFps)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {errorLabel ?? "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {currentPageData.page.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No jerboa client exports found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
